import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { completeLessonProgress } from '../engine/learningJourney'
import { recordLearningEvidence, type EvidenceKind } from '../engine/learningEvidence'
import { LocalStorageProgressRepository } from '../services/progressRepository'
import type { Lesson } from '../types/content'
import type { Progress } from '../types/progress'
import type { AttemptDetails } from '../types/progress'
import { localDateKey } from '../utils/date'

interface ProgressActions {
  recordAttempt(lesson: Lesson, correct: boolean, evidence?: EvidenceKind, details?: AttemptDetails): void
  completeLesson(lesson: Lesson): void
  addLearningSeconds(seconds: number): void
}
interface ProgressContextValue { progress: Progress; actions: ProgressActions; saveError: boolean }
const ProgressContext = createContext<ProgressContextValue | null>(null)
const repository = new LocalStorageProgressRepository()

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<Progress>(() => repository.load())
  const [saveError, setSaveError] = useState(false)
  useEffect(() => {
    try { repository.save(progress); setSaveError(false) } catch { setSaveError(true) }
  }, [progress])
  const recordAttempt = useCallback((lesson: Lesson, correct: boolean, evidence: EvidenceKind = 'checked', details?: AttemptDetails) => {
    setProgress((current) => recordLearningEvidence(current, lesson, correct, evidence, details))
  }, [])
  const completeLesson = useCallback((lesson: Lesson) => {
    setProgress((current) => completeLessonProgress(current, lesson, localDateKey()))
  }, [])
  const addLearningSeconds = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return
    setProgress((current) => {
      const date = localDateKey()
      const dailyRecords = current.dailyRecords.some((record) => record.date === date)
        ? current.dailyRecords.map((record) => record.date === date ? { ...record, seconds: record.seconds + Math.floor(seconds) } : record)
        : [...current.dailyRecords, { date, seconds: Math.floor(seconds), lessons: 0 }]
      return { ...current, dailyRecords: dailyRecords.slice(-14) }
    })
  }, [])
  const actions = useMemo(() => ({ recordAttempt, completeLesson, addLearningSeconds }), [recordAttempt, completeLesson, addLearningSeconds])
  const value = useMemo(() => ({ progress, actions, saveError }), [progress, actions, saveError])
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>
}
export const useProgress = () => {
  const value = useContext(ProgressContext)
  if (!value) throw new Error('useProgress must be used within ProgressProvider')
  return value
}
